/**
 * Organizes the /campaign-audit list: status tabs with counts, search, the
 * priority order and the KPI strip. Pure — the page passes the campaigns from
 * `adminCpmCampaigns` and renders the result.
 */

export type AuditCampaignStatus = 'active' | 'paused' | 'scheduled' | 'draft' | 'ended';
export type AuditStatusTab = 'all' | AuditCampaignStatus;

export type AuditCampaignSummary = {
  id: string;
  name: string;
  description?: string | null;
  status: string;
  createdAt?: string | null;
  pending_submissions_count?: number | null;
  pending_appeals_count?: number | null;
  pending_channel_requests_count?: number | null;
  youtube_channels?: Array<{ name?: string | null; handle?: string | null }> | null;
};

/** Tab order is also the working order used to sort campaigns with nothing waiting. */
export const AUDIT_STATUS_TABS: AuditStatusTab[] = ['all', 'active', 'paused', 'scheduled', 'draft', 'ended'];

const STATUS_ORDER: AuditCampaignStatus[] = ['active', 'paused', 'scheduled', 'draft', 'ended'];

function statusRank(status: string): number {
  const index = STATUS_ORDER.indexOf(status as AuditCampaignStatus);
  return index === -1 ? STATUS_ORDER.length : index;
}

function normalize(text: string | null | undefined): string {
  return (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

export function countAuditCampaignsByTab(campaigns: AuditCampaignSummary[]): Record<AuditStatusTab, number> {
  const counts: Record<AuditStatusTab, number> = {
    all: campaigns.length, active: 0, paused: 0, scheduled: 0, draft: 0, ended: 0,
  };
  for (const c of campaigns) {
    if (c.status in counts && c.status !== 'all') counts[c.status as AuditCampaignStatus] += 1;
  }
  return counts;
}

export function filterAuditCampaigns(
  campaigns: AuditCampaignSummary[],
  { tab, query }: { tab: AuditStatusTab; query: string },
): AuditCampaignSummary[] {
  const needle = normalize(query.trim());
  return campaigns.filter((c) => {
    if (tab !== 'all' && c.status !== tab) return false;
    if (!needle) return true;
    const haystacks = [
      c.name,
      c.description,
      ...(c.youtube_channels || []).flatMap((ch) => [ch.name, ch.handle]),
    ];
    return haystacks.some((text) => normalize(text).includes(needle));
  });
}

/** Clips, appeals or channel requests are waiting on an auditor. */
export function needsAuditAttention(c: AuditCampaignSummary): boolean {
  return (c.pending_submissions_count || 0) > 0
    || (c.pending_appeals_count || 0) > 0
    || (c.pending_channel_requests_count || 0) > 0;
}

/**
 * Work first: campaigns with anything waiting, most pending clips first. Then
 * the rest by status (active → paused → scheduled → draft → ended), newest
 * first. Returns a new array.
 */
export function sortAuditCampaignsByPriority(campaigns: AuditCampaignSummary[]): AuditCampaignSummary[] {
  return [...campaigns].sort((a, b) => {
    const attention = Number(needsAuditAttention(b)) - Number(needsAuditAttention(a));
    if (attention !== 0) return attention;
    const pending = (b.pending_submissions_count || 0) - (a.pending_submissions_count || 0);
    if (pending !== 0) return pending;
    const status = statusRank(a.status) - statusRank(b.status);
    if (status !== 0) return status;
    return (b.createdAt || '').localeCompare(a.createdAt || '');
  });
}

export type AuditKpis = {
  pendingClips: number;
  pendingAppeals: number;
  pendingChannelRequests: number;
  active: number;
  scheduled: number;
  draft: number;
  total: number;
};

export function summarizeAuditKpis(campaigns: AuditCampaignSummary[]): AuditKpis {
  const counts = countAuditCampaignsByTab(campaigns);
  return campaigns.reduce<AuditKpis>((kpis, c) => ({
    ...kpis,
    pendingClips: kpis.pendingClips + (c.pending_submissions_count || 0),
    pendingAppeals: kpis.pendingAppeals + (c.pending_appeals_count || 0),
    pendingChannelRequests: kpis.pendingChannelRequests + (c.pending_channel_requests_count || 0),
  }), {
    pendingClips: 0,
    pendingAppeals: 0,
    pendingChannelRequests: 0,
    active: counts.active,
    scheduled: counts.scheduled,
    draft: counts.draft,
    total: counts.all,
  });
}
